"use client";
import React from "react";
import Highcharts from "highcharts";
import { useEffect } from "react";
import { useState } from "react";
import dynamic from "next/dynamic";

const HighchartsReact = dynamic(() => import("highcharts-react-official"), {
  ssr: false,
});
const MfHighCharts: React.FC = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    import("highcharts/highcharts-more").then((mod) => {
      const highchartsMore =
        (mod as typeof import("highcharts/highcharts-more")).default || mod;

      if (typeof highchartsMore === "function") {
        highchartsMore(Highcharts);
      }

      setReady(true);
    });
  }, []);
  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      height: 250,
      spacing: [10, 20, 10, 20],
    },

    title: { text: "" },

    xAxis: {
      min: 0,
      max: 6,
      tickPositions: [0, 3, 6],
      labels: {
        style: {
          color: "#C0C0C0",
          fontSize: "14px",
          fontWeight: "bold",
        },
        formatter: function () {
          if (this.value === 0) return "TODAY";
          if (this.value === 3) return "YEAR 5";
          if (this.value === 6) return "YEAR 10";
          return "";
        },
        y: 30,
      },
      lineColor: "#1F2937",
      tickColor: "transparent",
    },

    yAxis: {
      title: { text: "" },
      gridLineColor: "#434343",
      gridLineWidth: 1,
      gridLineDashStyle: "Dash",
      min: 0,
      max: 8,
      tickPositions: [0, 2, 4, 6, 8],
      labels: { enabled: false },
      lineColor: "transparent",
    },

    legend: { enabled: false },
    tooltip: { enabled: false },

    plotOptions: {
      series: {
        marker: { enabled: false },
        clip: false,
        enableMouseTracking: false, 
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            enabled: false, 
          },
        },
      },
      line: {
        lineWidth: 2,
        linecap: "round",
      },
    },

    series: [
      {
        type: "line",
        name: "Gap Lines",
        data: [
          [5, 2.8],
          [5, 5],
        ],
        color: "#EF4444",
        lineWidth: 1,
        dashStyle: "Solid",
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        zIndex: 1,
      },
      {
        type: "arearange",
        name: "Gap",
        data: [
          [0, 0.3, 0.3],
          [1, 0.8, 1],
          [2, 1.3, 2],
          [3, 1.8, 3],
          [4, 2.3, 4],
          [5, 2.8, 5],
          [6, 3.3, 6],
        ],
        color: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, "rgba(239, 68, 68, 0.4)"],
            [1, "rgba(239, 68, 68, 0.05)"],
          ],
        } as Highcharts.GradientColorObject,
        lineWidth: 0,
        enableMouseTracking: false,
        zIndex: 0,
      },

      {
        type: "spline",
        name: "Current Path",
        data: [
          [0, 0.3],
          [1, 0.8],
          [2, 1.3],
          [3, 1.8],
          [4, 2.3],
          [5, 2.8],
          [6, 3.3], // cap at max x
          {
            x: 6, // marker exactly at max x
            y: 3.3,
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 6,
              fillColor: "#9CA3AF",
              lineColor: "#FFFFFF",
              lineWidth: 2,
            },
          },
        ],
        color: "#99A1AF",
        zIndex: 2,
      },

      {
        type: "spline",
        name: "Optimised Path",
        data: [
          [0, 0.3],
          [1.0, 1],
          [2.0, 2],
          [3.0, 3],
          [4.0, 4],
          [5.0, 5],
          [6, 6], // last point capped at xAxis.max
          {
            x: 6, // marker exactly at max x
            y: 6,
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 6,
              fillColor: "#59FF86",
              lineColor: "#59FF86",

              lineWidth: 2,
            },
          },
        ],
        color: "#59FF86",
        zIndex: 2,
        dataLabels: {
          enabled: true,
          useHTML: true,
          align: "left",
          x: 10,
          formatter: function () {
            if (this.x === 6) {
              return `<span style="
            background:#173411;
            color:#59FF86;
            padding:4px 10px;
            border-radius:999px;
               border:1px solid #59FF86;
            font-size:12px;
            font-weight:bold;
          ">Optimised Path</span>`;
            }
            return null;
          },
        },
      },
      {
        type: "scatter",
        data: [[5, 4]], // ✅ align with red line
        marker: { enabled: false },
        dataLabels: {
          enabled: true,
          useHTML: true,
          align: "center",
          verticalAlign: "middle",
          formatter: function () {
            return `<span style="
        background:#2D1414;
        color:#EC4D4D;
        padding:4px 10px;
        border-radius:999px;
        border: 1px solid #EC4D4D;
        font-size:12px;
        font:bold;
        font-weight:bold;
      ">GAP</span>`;
          },
        },
      },

      // ⚪ CURRENT LABEL
      {
        type: "scatter",
        data: [[6, 2.8]],
        marker: { enabled: false },
        dataLabels: {
          enabled: true,

          useHTML: true,
          y: 30,
          formatter: function () {
            return `<span style="
      
          background:#262626;
          color:#CBCBCB;
          padding:4px 10px;
          border-radius:999px;
          border:1px solid #CBCBCB;
          font-size:12px;
          font-weight:bold;
        ">Current Path</span>`;
          },
        },
      },
    ],

    credits: { enabled: false },
  };

  return (
    <div className="relative rounded-2xl  mt-8 w-full h-85 px-2 overflow-hidden">
      {ready && <HighchartsReact highcharts={Highcharts} options={options} />}
      <p className="text-center text-xs text-[#C0C0C0] -mt-2 xs:mt-3 lg:mt-2 italic leading-1.6">
        *For Illustrative purpose only
      </p>
    </div>
  );
};

export default MfHighCharts;
